import { OktaAuth } from '@okta/okta-auth-js';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as api } from '@mesa-labs/mesa-api';

import cognitoIdentityProvider from '../../cognito';
import * as types from './types';
import { facilitatorsApi } from '../api/facilitators';
import { RootState } from '../store';
import { Partners } from '@mesa-labs/mesa-api/dist/types';
import { AuthUser, IdAccessToken } from '@mesa-labs/mesa-ui';

type LogInMethod = 'OKTA' | 'COGNITO';

interface AuthState {
  isAuthorized: boolean | undefined;
  email: string | undefined;
  accessToken: string | undefined;
  accessTokenExpiresAt: number | undefined;
  roles: AuthenticationGroup[],
  isLoggedIn: boolean | undefined;
  partnerId?: number;
  attributes?: Record<string, any>;
  mfaOptions: string[],
  logInMethod?: LogInMethod;
  loading?: boolean;
  error?: Error;
  username?: string;
  isReadOnly?: boolean;
}

export enum AuthenticationGroup {
  OPERATORS = 'Operators',
  FINANCIERS = 'Financiers',
}

type AuthResult = {
  readonly username: string;
  readonly mfaOptions: string[];
  readonly isLoggedIn: boolean;
  readonly isReadOnly: boolean;
  readonly canViewBasicEntities: boolean;
  readonly canViewBusinessInternals: boolean;
  readonly canPerformFinancialOperations: boolean;
  readonly partnerId?: number;
  readonly roles: AuthenticationGroup[];
  readonly attributes?: Record<string, any>;
};

const initialState: AuthState = {
  isAuthorized: undefined,
  email: undefined,
  accessToken: undefined,
  accessTokenExpiresAt: undefined,
  roles: [],
  isLoggedIn: undefined,
  logInMethod: undefined,
  attributes: [],
  loading: false,
  partnerId: undefined,
  error: undefined,
  username: undefined,
  isReadOnly: undefined,
  mfaOptions: [],
};

const InternalGroups = new Set<string>(['Operators', 'Financiers']);

export const getImpersonatedVendorId = (attributes?: Record<string, any>): string | undefined => {
  return (attributes || {})['custom:vendorId'];
}

export const getMfaOptions = async (user: AuthUser): Promise<string[]> => user.getUserData().UserMFASettingList || [];

export const getRBAC = async (user: AuthUser, idToken: IdAccessToken): Promise<AuthResult> => {
  const groups = (idToken.payload['cognito:groups'] as AuthenticationGroup[]) || [];
  const intersection = new Set([...groups].filter((x) => InternalGroups.has(x)));
  if (intersection.size === 0) {
    await cognitoIdentityProvider.signOut();
    throw new Error('User does not have legal RBAC role.');
  }
  const attributes = user.getAttributes();
  const isReadOnly = attributes['custom:readOnly'] === 'true';
  const partnerId = parseInt(attributes['custom:partnerId'] as string | undefined || '', 10) || undefined;

  const isOperator = intersection.has(AuthenticationGroup.OPERATORS);
  const isFinancier = intersection.has(AuthenticationGroup.FINANCIERS);

  const canViewBasicEntities = isOperator || isFinancier;

  const canViewBusinessInternals = isOperator || isFinancier;

  const mfaOptions: string[] = await getMfaOptions(user);

  return {
    username: attributes.email! as string,  // email is required currently
    mfaOptions,
    isLoggedIn: true,
    isReadOnly,
    canViewBasicEntities,
    canViewBusinessInternals,
    canPerformFinancialOperations: isFinancier,
    partnerId,
    roles: [...intersection],
    attributes,
  };
};

export const cognitoSignIn = createAsyncThunk(
  'auth/signIn',
  async (args: {
    email: string,
    password: string,
    partnerId: api.Partners,
  }, { dispatch }): Promise<AuthResult> => {
    const { email, password } = args;

    await cognitoIdentityProvider.signIn(email, password, null, () => Promise.resolve(undefined));
    await dispatch(facilitatorsApi.endpoints.impersonateFacilitator.initiate({ partnerId: args.partnerId }));
    await dispatch(setupCognito({ forceRefresh: true }));

    const session = cognitoIdentityProvider.getCurrentSession();
    if (!session) {
      throw new Error('No session found after signIn');
    }

    const user = cognitoIdentityProvider.getCurrentUser();
    if (!user) {
      throw new Error('No user found after signIn');
    }

    return await getRBAC(user, session.getIdToken());
  },
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (oktaAuth: OktaAuth, { dispatch, getState }): Promise<void> => {
    const state = getState() as RootState;

    if (state.auth.logInMethod === 'COGNITO') {
      if (state.auth.partnerId) {
        await dispatch(facilitatorsApi.endpoints.endFacilitatorImpersonation.initiate({ partnerId: state.auth.partnerId }));
      }

      await cognitoIdentityProvider.signOut();
    }

    if (state.auth.logInMethod === 'OKTA') {
      await oktaAuth.signOut();
    }
  },
);

export const setupCognito = createAsyncThunk(
  'auth/setupCognito',
  async (args: { forceRefresh: boolean }): Promise<AuthResult> => {
    let session = cognitoIdentityProvider.getCurrentSession();
    if (!session) {
      throw new Error('No session found during setupCognito');
    }

    const forceRefresh = args.forceRefresh;
    if (forceRefresh || !session.isValid()) {
      session = await cognitoIdentityProvider.refreshCurrentSession();
      if (!session) {
        throw new Error('Failed to refresh session found during setupCognito');
      }
    }

    const user = cognitoIdentityProvider.getCurrentUser();
    if (!user) {
      throw new Error('No user found during setupCognito');
    }

    return await getRBAC(user, session.getIdToken());
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // OKTA login
    updateAccessToken(state: AuthState, action: PayloadAction<{ email: string, tokenContainer: api.TokenContainer, partnerId: Partners } | undefined>) {
      state.isAuthorized = !!action.payload;
      state.email = action.payload?.email;
      state.accessToken = action.payload?.tokenContainer.token;
      state.accessTokenExpiresAt = action.payload?.tokenContainer.expiresAt;
      state.logInMethod = 'OKTA';
      state.partnerId = action.payload?.partnerId;
    },
    updateLogInMethod(state: AuthState, action: PayloadAction<LogInMethod>) {
      state.logInMethod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setupCognito.fulfilled, (state: AuthState, action) => {
        state.isLoggedIn = action.payload.isLoggedIn;
        state.roles = action.payload.roles;
        state.attributes = action.payload.attributes;
      })
      .addCase(setupCognito.rejected, (state: AuthState) => {
        state.isLoggedIn = false;
      })
      // COGNITO login
      .addCase(cognitoSignIn.fulfilled, (state: AuthState, action) => {
        state.username = action.payload.username;
        state.mfaOptions = action.payload.mfaOptions;
        state.isLoggedIn = action.payload.isLoggedIn;
        state.isReadOnly = action.payload.isReadOnly;
        state.partnerId = action.payload.partnerId;
        state.roles = action.payload.roles;
        state.attributes = action.payload.attributes;
        state.isAuthorized = true;
        state.logInMethod = 'COGNITO';
      })
      .addCase(cognitoSignIn.rejected, (state: AuthState) => {
        state.isLoggedIn = false;
        state.isAuthorized = false;
      })
      .addCase(signOut.fulfilled, (state: AuthState, _action: PayloadAction<void>) => {
        state.isAuthorized = false;
        state.isLoggedIn = false;
        state.email = undefined;
        state.accessToken = undefined;
        state.accessTokenExpiresAt = undefined;
        state.logInMethod = undefined;
      })
      .addCase(signOut.rejected, (state: AuthState, _error: any) => {
        state.isAuthorized = false;
        state.isLoggedIn = false;
        state.email = undefined;
        state.accessToken = undefined;
        state.accessTokenExpiresAt = undefined;
        state.logInMethod = undefined;
      })
      .addMatcher((action) => types.isPendingAction(action, 'auth'), (state: AuthState) => {
        state.loading = true;
      })
      .addMatcher((action) => types.isRejectedAction(action, 'auth'), (state: AuthState, action) => {
        state.loading = false;
        state.error = <Error>action.error;
      })
      .addMatcher((action) => types.isFulfilledAction(action, 'auth'), (state: AuthState) => {
        state.loading = false;
        state.error = undefined;
      });
  },
});

export const {
  updateAccessToken,
  updateLogInMethod,
} = authSlice.actions;

export default authSlice.reducer;
